import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorSchema = z.object({ message: z.string() });

const machaIdSchema = z.string().regex(/^mcha_[0-9A-Za-z]{22}$/);
const machaSessionIdSchema = z.string().regex(/^mses_[0-9A-Za-z]{22}$/);
const machaArtifactIdSchema = z.string().regex(/^(mart|mws)_[0-9A-Za-z]{22}$/);
const machaCommentIdSchema = z.string().regex(/^(mcom|mwsc)_[0-9A-Za-z]{22}$/);
const machaEventIdSchema = z.string().regex(/^mev_[0-9A-Za-z]{22}$/);
const machaWatchIdSchema = z.string().regex(/^mwt_[0-9A-Za-z]{22}$/);
const machaNotificationIdSchema = z.string().regex(/^mnt_[0-9A-Za-z]{22}$/);

export const machaStatusValues = ['provisioning', 'ready', 'stopped'] as const;
export const machaStatusSchema = z.enum(machaStatusValues);

export const machaMemberRoleValues = ['owner', 'member'] as const;
export const machaMemberRoleSchema = z.enum(machaMemberRoleValues);

export const machaArtifactTypeValues = [
  'note',
  'doc',
  'link',
  'repo',
  'task',
  'app',
  'thread',
  'workstream',
] as const;
export const machaArtifactTypeSchema = z.enum(machaArtifactTypeValues);

export const machaWorkstreamIdSchema = z.string().regex(/^mws_[0-9A-Za-z]{22}$/);

export const machaWorkstreamHarnessValues = ['claude-code', 'codex', 'amp'] as const;
export const machaWorkstreamHarnessSchema = z.enum(machaWorkstreamHarnessValues);

export const machaWorkstreamStatusValues = [
  'provisioning',
  'drafting',
  'idle',
  'running',
  'verifying',
  'needs_review',
  'ready',
  'blocked',
  'failed',
  'archived',
] as const;
export const machaWorkstreamStatusSchema = z.enum(machaWorkstreamStatusValues);

/**
 * Long-lived feature-work state for 'workstream' artifacts: a git
 * worktree + a resumable multi-turn coding-agent session + PR/issue
 * metadata, rendered as a live card. Turns execute via the workstream
 * orchestrator (v1 runner: 'local').
 */
export const machaWorkstreamSchema = z.object({
  // The workstream's own id (mws_…), used for the
  // /machas/workstreams/:workstreamId/* endpoints.
  id: z.string(),
  repo_path: z.string(),
  // Empty until provisioning has created the managed worktree.
  worktree_path: z.string(),
  branch: z.string(),
  harness: machaWorkstreamHarnessSchema,
  // Where turns execute; v1 is always 'local' (the dev API process).
  runner: z.string(),
  task: z.string(),
  status: machaWorkstreamStatusSchema,
  pr_url: z.string(),
  pr_state: z.string(),
  // Rollup of PR checks: 'passing' | 'failing' | 'pending' | ''.
  pr_checks: z.string(),
  linear_issue_url: z.string(),
  last_error: z.string(),
  last_activity_at: z.string(),
  message_count: z.number().int(),
});

export const machaMemberSchema = z.object({
  user_id: z.string().min(1),
  display_name: z.string().min(1),
  handle: z.string().nullable(),
  // Browser-fetchable presigned avatar URL when the user has uploaded
  // a profile picture; null when they haven't.
  avatar_url: z.string().nullable(),
  role: machaMemberRoleSchema,
});

// The org member a canvas belongs to. Every member owns exactly one
// canvas, so the canvas list is the team roster. Distinct from the
// assistant `name` (e.g. "Aurora").
export const machaOwnerSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().min(1),
  avatar_url: z.string().nullable(),
});

// Per-kind decision counts (all six kinds, zeros included) rolled up
// across a canvas's linked thread artifacts. Web consumes this everywhere
// asked/corrected used to appear.
export const machaDecisionCountsSchema = z.object({
  asked: z.number().int().nonnegative(),
  requested: z.number().int().nonnegative(),
  decided: z.number().int().nonnegative(),
  corrected: z.number().int().nonnegative(),
  shared: z.number().int().nonnegative(),
  requirement: z.number().int().nonnegative(),
});

export const machaListObjectSchema = z.object({
  id: machaIdSchema,
  name: z.string().min(1),
  status: machaStatusSchema,
  // The owning member for roster canvases; null for extra canvases
  // created explicitly via createMacha (they list after the roster).
  owner: machaOwnerSchema.nullable(),
  member_count: z.number().int().nonnegative(),
  artifact_count: z.number().int().nonnegative(),
  // Per-kind decision-count rollup summed across the canvas's linked
  // thread artifacts.
  decision_counts: machaDecisionCountsSchema,
  created_at: z.string().datetime(),
});

export const machaListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('macha'),
  objects: z.array(machaListObjectSchema),
});

export const machaDetailResponseSchema = z.object({
  id: machaIdSchema,
  name: z.string().min(1),
  status: machaStatusSchema,
  // Whether an OpenClaw gateway is configured. The token itself is
  // never exposed on the contract.
  has_gateway: z.boolean(),
  // The owning member. Null only for legacy unowned canvases reached by
  // direct URL.
  owner: machaOwnerSchema.nullable(),
  member_count: z.number().int().nonnegative(),
  artifact_count: z.number().int().nonnegative(),
  // Per-kind decision-count rollup summed across the canvas's linked
  // thread artifacts.
  decision_counts: machaDecisionCountsSchema,
  created_at: z.string().datetime(),
  members: z.array(machaMemberSchema),
});

export const createMachaRequestSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1)
    .max(120)
    .describe('Canvas name (doubles as the assistant name for this canvas)'),
});

export const addMachaMemberRequestSchema = z.object({
  user_id: z.string().min(1).describe('Lore user id (user_…) to add to this macha'),
});

export const addMachaMemberResponseSchema = z.object({
  members: z.array(machaMemberSchema),
});

export const machaArtifactSchema = z.object({
  id: machaArtifactIdSchema,
  macha_id: machaIdSchema,
  session_id: machaSessionIdSchema.nullable(),
  type: machaArtifactTypeSchema,
  title: z.string(),
  body: z.string(),
  url: z.string().nullable(),
  x: z.number().int(),
  y: z.number().int(),
  created_by_agent: z.boolean(),
  // Display name of the creating user; null for agent-created artifacts
  // (and for deleted users).
  creator_name: z.string().nullable(),
  // The Lore thread this card belongs to (thread cards and the run
  // output/verification apps derived from a run); null otherwise.
  thread_id: z.string().nullable(),
  // Data-flow lineage: the artifact this one was derived from (e.g. a
  // verification tool's source is its run's result app).
  source_artifact_id: z.string().nullable(),
  thread_title: z.string().nullable(),
  // Per-kind decision counts for the linked thread. Non-null only for
  // thread artifacts.
  decision_counts: machaDecisionCountsSchema.nullable(),
  // Long-lived workstream state. Non-null only for workstream artifacts.
  workstream: machaWorkstreamSchema.nullable(),
  // Last app-content change (max across the app's files); null for
  // non-app artifacts. The web keys the live preview iframe on this.
  app_content_updated_at: z.string().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const machaArtifactListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('macha_artifact'),
  objects: z.array(machaArtifactSchema),
});

export const createMachaArtifactRequestSchema = z.object({
  type: machaArtifactTypeSchema,
  // Required for every type except 'thread', where it defaults to the
  // linked thread's title. Enforced by the handler (400).
  title: z
    .string()
    .trim()
    .min(1)
    .max(200)
    .optional()
    .describe('Card title; optional for thread artifacts (defaults to the thread title)'),
  body: z.string().max(20000).optional(),
  url: z.string().max(2000).optional(),
  thread_id: z
    .string()
    .optional()
    .describe(
      'Lore thread id (th_…) to pin to the board. Required when type is "thread"; rejected otherwise.',
    ),
  x: z.number().int(),
  y: z.number().int(),
});

export const updateMachaArtifactRequestSchema = z
  .object({
    x: z.number().int().optional(),
    y: z.number().int().optional(),
    title: z.string().trim().min(1).max(200).optional(),
    body: z.string().max(20000).optional(),
    url: z.string().max(2000).nullable().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be provided',
  });

export const deleteMachaArtifactResponseSchema = z.object({
  deleted: z.literal(true),
});

export const machaArtifactTurnRequestSchema = z.object({
  message: z
    .string()
    .trim()
    .min(1)
    .max(8000)
    .describe("A message to the card's authoring agent; may ask to change the app"),
});

export const machaArtifactTurnResponseSchema = z.object({
  reply: z.string(),
  // True when the turn rewrote the card's app bundle (the iframe should reload).
  updated: z.boolean(),
});

export const createMachaWorkflowRequestSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1)
    .max(80)
    .describe('Workflow name (becomes the skill name, slugified)'),
  automates: z
    .string()
    .trim()
    .min(1)
    .max(4000)
    .describe('What the automation does, in plain language (steps welcome)'),
  inputs: z
    .string()
    .trim()
    .max(2000)
    .optional()
    .describe('What each run needs as input (defaults to "provided in the operator notes")'),
  checks: z
    .array(z.string().trim().min(1).max(300))
    .max(20)
    .default([])
    .describe(
      'Verification rules the paired checker app must enforce before the output ships (empty = the agent derives sensible checks)',
    ),
});

export const machaWorkflowResponseSchema = z.object({
  skill_id: z.string(),
  name: z.string(),
  description: z.string(),
});

export const createMachaSkillRunRequestSchema = z.object({
  skill_id: z
    .string()
    .describe('Skill id (sk_…) to launch as an agent process'),
  notes: z
    .string()
    .max(8000)
    .optional()
    .describe('Operator notes threaded into the process prompt and the app spec'),
  x: z.number().int(),
  y: z.number().int(),
});

export const createMachaWorkstreamRequestSchema = z.object({
  repo_path: z
    .string()
    .min(1)
    .describe('Absolute path to an existing git repository on the dev machine'),
  task: z
    .string()
    .trim()
    .min(1)
    .max(8000)
    .describe('The feature-work task; doubles as the first user message'),
  title: z
    .string()
    .max(200)
    .optional()
    .describe('Card title; defaults to the first 60 chars of the task'),
  linear_issue_url: z
    .union([z.string().url(), z.literal('')])
    .optional()
    .describe('Linked Linear issue URL, or empty for none'),
  harness: machaWorkstreamHarnessSchema
    .optional()
    .describe("Coding agent driving the workstream; defaults to 'claude-code'"),
  x: z.number().int(),
  y: z.number().int(),
});

export const machaWorkstreamMessageSchema = z.object({
  id: z.string().regex(/^mwsm_[0-9A-Za-z]{22}$/),
  role: z.enum(['user', 'assistant']),
  author_user_id: z.string().nullable(),
  // Display name of the authoring user; null for assistant messages
  // (and deleted users).
  author_name: z.string().nullable(),
  content: z.string(),
  created_at: z.string().datetime(),
});

export const machaWorkstreamMessageListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('macha_workstream_message'),
  objects: z.array(machaWorkstreamMessageSchema),
});

export const postMachaWorkstreamMessageRequestSchema = z.object({
  content: z.string().trim().min(1).max(8000),
});

export const postMachaWorkstreamMessageResponseSchema = z.object({
  // The turn executes asynchronously; poll the artifact/messages.
  status: z.literal('running'),
});

export const machaAppFileResponseSchema = z.object({
  path: z.string(),
  content: z.string(),
  updated_at: z.string().datetime(),
});

export const machaSessionSchema = z.object({
  id: machaSessionIdSchema,
  macha_id: machaIdSchema,
  title: z.string(),
  created_at: z.string().datetime(),
  message_count: z.number().int().nonnegative(),
});

export const machaSessionListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('macha_session'),
  objects: z.array(machaSessionSchema),
});

export const createMachaSessionRequestSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
});

export const machaMessageSchema = z.object({
  id: z.string().regex(/^mmsg_[0-9A-Za-z]{22}$/),
  session_id: machaSessionIdSchema,
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  // Display name of the authoring user; null for assistant messages.
  author_name: z.string().nullable(),
  created_at: z.string().datetime(),
});

export const machaMessageListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('macha_message'),
  objects: z.array(machaMessageSchema),
});

export const postMachaMessageRequestSchema = z.object({
  content: z.string().trim().min(1).max(20000),
});

export const postMachaMessageResponseSchema = z.object({
  // Exactly [userMessage, assistantMessage] for this turn.
  messages: z.array(machaMessageSchema),
  // Artifacts the agent placed on the board during this turn.
  artifacts_created: z.array(machaArtifactSchema),
  // Existing artifacts the agent modified during this turn (e.g. an
  // update_app rewrite). Disjoint from artifacts_created.
  artifacts_updated: z.array(machaArtifactSchema),
});

export const machaPresenceObjectSchema = z.object({
  user_id: z.string().min(1),
  display_name: z.string().min(1),
  // Board world coordinates. Null x/y means the user is present on the
  // board but their cursor is off-canvas.
  x: z.number().int().nullable(),
  y: z.number().int().nullable(),
  updated_at: z.string().datetime(),
});

export const machaPresenceListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('macha_presence'),
  // Includes the viewer's own cursor; clients filter self out.
  objects: z.array(machaPresenceObjectSchema),
});

export const updateMachaPresenceRequestSchema = z.object({
  x: z.number().int().nullable().describe('Cursor x in board world coordinates; null when off-canvas'),
  y: z.number().int().nullable().describe('Cursor y in board world coordinates; null when off-canvas'),
});

export const updateMachaPresenceResponseSchema = z.object({
  ok: z.literal(true),
});

export const machaCommentSchema = z.object({
  id: machaCommentIdSchema,
  macha_id: machaIdSchema,
  // The canvas card/workstream this comment annotates; null for legacy board-level pins.
  artifact_id: machaArtifactIdSchema.nullable(),
  // Provenance thread/block for comments attached to a workstream; null for legacy pins.
  thread_id: z.string().nullable(),
  thread_block_id: z.string().nullable(),
  // Figma-style pin position in board world coordinates.
  x: z.number().int(),
  y: z.number().int(),
  content: z.string(),
  // Display name of the authoring user; null for deleted users.
  author_name: z.string().nullable(),
  author_user_id: z.string().nullable(),
  created_at: z.string().datetime(),
  // Null while the comment is open.
  resolved_at: z.string().datetime().nullable(),
});

export const machaCommentListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('macha_comment'),
  objects: z.array(machaCommentSchema),
});

export const createMachaCommentRequestSchema = z.object({
  x: z.number().int().describe('Pin x in board world coordinates'),
  y: z.number().int().describe('Pin y in board world coordinates'),
  content: z.string().trim().min(1).max(4000),
  artifact_id: machaArtifactIdSchema
    .optional()
    .describe('Canvas card id (mws_/mart_…) this comment annotates, when the pin is on a card'),
});

export const deleteMachaCommentResponseSchema = z.object({
  deleted: z.literal(true),
});

export const machaEventKindValues = [
  'artifact_created',
  'run_started',
  'run_succeeded',
  'run_failed',
  'comment_created',
  'comment_resolved',
  'session_created',
  'thread_pinned',
  'member_added',
] as const;
export const machaEventKindSchema = z.enum(machaEventKindValues);

/**
 * One board-activity feed entry. This is the machas surface's own
 * domain activity, not the realtime event spine.
 */
export const machaEventSchema = z.object({
  id: machaEventIdSchema,
  macha_id: machaIdSchema,
  macha_name: z.string(),
  kind: machaEventKindSchema,
  // Null actor = the board assistant/agent (clients render 'Assistant').
  actor_user_id: z.string().nullable(),
  actor_name: z.string().nullable(),
  // Human-readable one-liner, rendered verbatim.
  summary: z.string(),
  ref_artifact_id: z.string().nullable(),
  created_at: z.string().datetime(),
});

export const machaEventListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('macha_event'),
  objects: z.array(machaEventSchema),
});

export const machaWatchSchema = z.object({
  id: machaWatchIdSchema,
  // Null = all boards in the viewer's org.
  macha_id: machaIdSchema.nullable(),
  macha_name: z.string().nullable(),
  // Comma-separated event kinds; empty string = all kinds.
  kinds: z.string(),
  created_at: z.string().datetime(),
});

export const machaWatchListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('macha_watch'),
  objects: z.array(machaWatchSchema),
});

export const createMachaWatchRequestSchema = z.object({
  macha_id: machaIdSchema
    .nullable()
    .optional()
    .describe('Board to watch; null/omitted = all boards in the org'),
  kinds: z
    .string()
    .max(500)
    .describe("Comma-separated event kinds to notify on; empty string = all kinds"),
});

export const deleteMachaWatchResponseSchema = z.object({
  deleted: z.literal(true),
});

export const machaNotificationSchema = z.object({
  id: machaNotificationIdSchema,
  seen_at: z.string().datetime().nullable(),
  created_at: z.string().datetime(),
  event: machaEventSchema,
});

export const machaNotificationListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('macha_notification'),
  unseen_count: z.number().int().nonnegative(),
  objects: z.array(machaNotificationSchema),
});

export const markMachaNotificationsSeenResponseSchema = z.object({
  seen: z.number().int().nonnegative(),
});

const machaHealthReportMetricsShape = {
  runs_total: z.number().int().nonnegative(),
  runs_succeeded: z.number().int().nonnegative(),
  runs_failed: z.number().int().nonnegative(),
  artifacts_created: z.number().int().nonnegative(),
  comments_opened: z.number().int().nonnegative(),
  comments_resolved: z.number().int().nonnegative(),
  sessions_created: z.number().int().nonnegative(),
  // Per-kind decision-count rollup across the board's linked threads.
  decision_counts: machaDecisionCountsSchema,
  active_member_count: z.number().int().nonnegative(),
};

export const machaHealthReportBoardSchema = z.object({
  macha_id: machaIdSchema,
  name: z.string(),
  // The teammate whose canvas this is; null only for explicitly
  // requested legacy unowned canvases.
  owner: machaOwnerSchema.nullable(),
  ...machaHealthReportMetricsShape,
});

export const machaHealthReportTotalsSchema = z.object(
  machaHealthReportMetricsShape,
);

export const machaHealthReportResponseSchema = z.object({
  window_hours: z.number().int().positive(),
  generated_at: z.string().datetime(),
  boards: z.array(machaHealthReportBoardSchema),
  totals: machaHealthReportTotalsSchema,
  // LLM ops summary; null when OPENAI_API_KEY is unset or the call fails.
  narrative: z.string().nullable(),
});

export const machaBootstrapResponseSchema = z.object({
  machas: z.number().int().nonnegative(),
  artifacts: z.number().int().nonnegative(),
  sessions: z.number().int().nonnegative(),
  // Distinct users holding a membership across the org's machas.
  members: z.number().int().nonnegative(),
});

export const machasContract = c.router({
  listMachas: {
    method: 'GET',
    path: '/machas',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaListResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'List machas (shared agent workspaces) in the authenticated user’s organization',
  },
  createMacha: {
    method: 'POST',
    path: '/machas',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: createMachaRequestSchema,
    responses: {
      201: machaDetailResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Create an additional canvas beyond the auto-provisioned per-member one. The creator joins as owner-member; the canvas lists after the roster.',
  },
  machaBootstrap: {
    method: 'GET',
    path: '/machas/bootstrap',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaBootstrapResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Org-scoped rollup counts of machas, artifacts, sessions, and distinct members',
  },
  listMachaWorkstreamMessages: {
    method: 'GET',
    path: '/machas/workstreams/:workstreamId/messages',
    pathParams: z.object({
      workstreamId: machaWorkstreamIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaWorkstreamMessageListResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'List a workstream’s conversation transcript, oldest first (max 200)',
  },
  postMachaWorkstreamMessage: {
    method: 'POST',
    path: '/machas/workstreams/:workstreamId/messages',
    pathParams: z.object({
      workstreamId: machaWorkstreamIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: postMachaWorkstreamMessageRequestSchema,
    responses: {
      202: postMachaWorkstreamMessageResponseSchema,
      400: errorSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
      409: errorSchema,
    },
    summary:
      'Post a user message and run the next coding-agent turn (resumes the CLI session). 409 while a turn is already running; 400 when archived.',
  },
  syncMachaWorkstreamPr: {
    method: 'POST',
    path: '/machas/workstreams/:workstreamId/sync-pr',
    pathParams: z.object({
      workstreamId: machaWorkstreamIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: machaArtifactSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Refresh the workstream’s PR metadata from `gh pr view` (url, state, checks rollup). No PR yet clears the fields.',
  },
  archiveMachaWorkstream: {
    method: 'POST',
    path: '/machas/workstreams/:workstreamId/archive',
    pathParams: z.object({
      workstreamId: machaWorkstreamIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: machaArtifactSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Archive a workstream: remove its managed worktree (best effort) and stop accepting turns',
  },
  listMachaEvents: {
    method: 'GET',
    // Static path segment coexists with /machas/:machaId the same way
    // /machas/bootstrap does (static routes win over params).
    path: '/machas/events',
    query: z.object({
      macha_id: machaIdSchema.optional().describe('Restrict the feed to one board'),
      kinds: z
        .string()
        .optional()
        .describe('Comma-separated event kinds to include; omitted = all kinds'),
      limit: z.coerce
        .number()
        .int()
        .positive()
        .max(200)
        .optional()
        .describe('Max events to return (default 50, max 200)'),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaEventListResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Org-wide board activity feed (all machas in the viewer’s organization), newest first',
  },
  listMachaWatches: {
    method: 'GET',
    path: '/machas/watches',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaWatchListResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'List the viewer’s watch rules (board-event notification subscriptions)',
  },
  createMachaWatch: {
    method: 'POST',
    path: '/machas/watches',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: createMachaWatchRequestSchema,
    responses: {
      201: machaWatchSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Create a watch rule: notify the viewer about matching board events (null board = all boards; empty kinds = all kinds)',
  },
  deleteMachaWatch: {
    method: 'DELETE',
    path: '/machas/watches/:watchId',
    pathParams: z.object({
      watchId: machaWatchIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: deleteMachaWatchResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Delete one of the viewer’s own watch rules',
  },
  listMachaNotifications: {
    method: 'GET',
    path: '/machas/notifications',
    query: z.object({
      unseen_only: z.coerce
        .boolean()
        .optional()
        .describe('Return only unseen notifications'),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaNotificationListResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'List the viewer’s notifications (newest first, capped at 100) with the unseen count',
  },
  markMachaNotificationsSeen: {
    method: 'POST',
    path: '/machas/notifications/seen',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: markMachaNotificationsSeenResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Mark all of the viewer’s unseen notifications as seen',
  },
  machaHealthReport: {
    method: 'GET',
    path: '/machas/health-report',
    query: z.object({
      window_hours: z.coerce
        .number()
        .int()
        .positive()
        .max(24 * 90)
        .optional()
        .describe('Reporting window in hours (default 168 = one week)'),
      macha_ids: z
        .string()
        .optional()
        .describe('Comma-separated macha ids to report on; omitted = all org boards'),
      // coerce first: the query parser turns "0" into number 0.
      narrative: z.coerce
        .string()
        .pipe(z.enum(['0', '1']))
        .optional()
        .describe('Set 0 to skip the LLM narrative (fast, metrics only). Default 1.'),
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaHealthReportResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Computed per-board ops health metrics over a time window, with an optional LLM narrative',
  },
  getMacha: {
    method: 'GET',
    path: '/machas/:machaId',
    pathParams: z.object({
      machaId: machaIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaDetailResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Get a macha with its members',
  },
  addMachaMember: {
    method: 'POST',
    path: '/machas/:machaId/members',
    pathParams: z.object({
      machaId: machaIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: addMachaMemberRequestSchema,
    responses: {
      200: addMachaMemberResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Add an org user to a macha as a member. Idempotent.',
  },
  listMachaArtifacts: {
    method: 'GET',
    path: '/machas/:machaId/artifacts',
    pathParams: z.object({
      machaId: machaIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaArtifactListResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'List the artifacts on a macha’s spatial board',
  },
  createMachaArtifact: {
    method: 'POST',
    path: '/machas/:machaId/artifacts',
    pathParams: z.object({
      machaId: machaIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: createMachaArtifactRequestSchema,
    responses: {
      201: machaArtifactSchema,
      400: errorSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Place a new artifact on the macha board',
  },
  updateMachaArtifact: {
    method: 'PATCH',
    path: '/machas/:machaId/artifacts/:artifactId',
    pathParams: z.object({
      machaId: machaIdSchema,
      artifactId: machaArtifactIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: updateMachaArtifactRequestSchema,
    responses: {
      200: machaArtifactSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Update an artifact’s board position or content',
  },
  deleteMachaArtifact: {
    method: 'DELETE',
    path: '/machas/:machaId/artifacts/:artifactId',
    pathParams: z.object({
      machaId: machaIdSchema,
      artifactId: machaArtifactIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: deleteMachaArtifactResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Remove an artifact from the macha board',
  },
  machaArtifactTurn: {
    method: 'POST',
    path: '/machas/:machaId/artifacts/:artifactId/turn',
    pathParams: z.object({
      machaId: machaIdSchema,
      artifactId: machaArtifactIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: machaArtifactTurnRequestSchema,
    responses: {
      200: machaArtifactTurnResponseSchema,
      400: errorSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: "Run one authoring turn on a card's thread; may rewrite its app",
  },
  createMachaWorkstream: {
    method: 'POST',
    path: '/machas/:machaId/workstreams',
    pathParams: z.object({
      machaId: machaIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: createMachaWorkstreamRequestSchema,
    responses: {
      201: machaArtifactSchema,
      400: errorSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Start a long-lived workstream: a managed git worktree + resumable coding-agent session, tracked as a live board card. The task is the first user message; provisioning and the first turn run asynchronously.',
  },
  createMachaSkillRun: {
    method: 'POST',
    path: '/machas/:machaId/skill-runs',
    pathParams: z.object({
      machaId: machaIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: createMachaSkillRunRequestSchema,
    responses: {
      201: machaArtifactSchema,
      400: errorSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Launch a skill as an agent on the canvas: creates a Lore-native thread running the process server-side (thread card at x,y); on completion the run’s RESULT app (x+340,y) and its paired VERIFICATION tool (x+680,y) materialize, both linked to the thread. Returns the thread artifact immediately.',
  },
  createMachaWorkflow: {
    method: 'POST',
    // Static path segment coexists with /machas/:machaId the same way
    // /machas/bootstrap does (static routes win over params). Workflows
    // are org-level skills, not per-board state, so no :machaId.
    path: '/machas/workflows',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: createMachaWorkflowRequestSchema,
    responses: {
      201: machaWorkflowResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Create a verified-workflow skill from the opinionated template: an automation plus the verification contract its paired checker app must enforce. The skill lands in the Run-a-process picker; each run produces the output app AND its verification tool.',
  },
  getMachaAppFile: {
    method: 'GET',
    path: '/machas/:machaId/artifacts/:artifactId/app',
    pathParams: z.object({
      machaId: machaIdSchema,
      artifactId: machaArtifactIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaAppFileResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Get an app artifact’s index.html source (JSON-wrapped). The raw, iframe-servable copy lives at /api/macha-apps/:artifactId.',
  },
  updateMachaPresence: {
    method: 'POST',
    path: '/machas/:machaId/presence',
    pathParams: z.object({
      machaId: machaIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: updateMachaPresenceRequestSchema,
    responses: {
      200: updateMachaPresenceResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Report the viewer’s cursor position on the macha board (ephemeral; entries expire after 15s)',
  },
  listMachaPresence: {
    method: 'GET',
    path: '/machas/:machaId/presence',
    pathParams: z.object({
      machaId: machaIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaPresenceListResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'List live cursors on the macha board (fresher than 15s, including the viewer)',
  },
  listMachaComments: {
    method: 'GET',
    path: '/machas/:machaId/comments',
    pathParams: z.object({
      machaId: machaIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaCommentListResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'List the macha board’s comment pins (open and resolved), oldest first',
  },
  createMachaComment: {
    method: 'POST',
    path: '/machas/:machaId/comments',
    pathParams: z.object({
      machaId: machaIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: createMachaCommentRequestSchema,
    responses: {
      201: machaCommentSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Pin a new comment on the macha board',
  },
  resolveMachaComment: {
    method: 'POST',
    path: '/machas/:machaId/comments/:commentId/resolve',
    pathParams: z.object({
      machaId: machaIdSchema,
      commentId: machaCommentIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: machaCommentSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Mark a board comment resolved. Idempotent.',
  },
  deleteMachaComment: {
    method: 'DELETE',
    path: '/machas/:machaId/comments/:commentId',
    pathParams: z.object({
      machaId: machaIdSchema,
      commentId: machaCommentIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: deleteMachaCommentResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Delete a board comment. Any org member may delete (v1).',
  },
  listMachaSessions: {
    method: 'GET',
    path: '/machas/:machaId/sessions',
    pathParams: z.object({
      machaId: machaIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaSessionListResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'List a macha’s chat sessions, newest first',
  },
  createMachaSession: {
    method: 'POST',
    path: '/machas/:machaId/sessions',
    pathParams: z.object({
      machaId: machaIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: createMachaSessionRequestSchema,
    responses: {
      201: machaSessionSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Start a new chat session with the macha’s primary assistant',
  },
  listMachaMessages: {
    method: 'GET',
    path: '/machas/:machaId/sessions/:sessionId/messages',
    pathParams: z.object({
      machaId: machaIdSchema,
      sessionId: machaSessionIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: machaMessageListResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'List a session’s messages, oldest first',
  },
  postMachaMessage: {
    method: 'POST',
    path: '/machas/:machaId/sessions/:sessionId/messages',
    pathParams: z.object({
      machaId: machaIdSchema,
      sessionId: machaSessionIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: postMachaMessageRequestSchema,
    responses: {
      201: postMachaMessageResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary:
      'Post a user message and run the macha’s agent turn (gateway → OpenAI → deterministic local fallback). Returns both persisted messages plus any artifacts the agent created.',
  },
});

export type MachaStatus = z.infer<typeof machaStatusSchema>;
export type MachaMemberRole = z.infer<typeof machaMemberRoleSchema>;
export type MachaArtifactType = z.infer<typeof machaArtifactTypeSchema>;
export type MachaWorkstreamHarness = z.infer<typeof machaWorkstreamHarnessSchema>;
export type MachaWorkstreamStatus = z.infer<typeof machaWorkstreamStatusSchema>;
export type MachaWorkstreamResource = z.infer<typeof machaWorkstreamSchema>;
export type CreateMachaWorkstreamRequest = z.infer<
  typeof createMachaWorkstreamRequestSchema
>;
export type CreateMachaWorkflowRequest = z.infer<typeof createMachaWorkflowRequestSchema>;
export type MachaWorkflowResponse = z.infer<typeof machaWorkflowResponseSchema>;
export type CreateMachaSkillRunRequest = z.infer<
  typeof createMachaSkillRunRequestSchema
>;
export type MachaWorkstreamMessageResource = z.infer<
  typeof machaWorkstreamMessageSchema
>;
export type MachaWorkstreamMessageListResponse = z.infer<
  typeof machaWorkstreamMessageListResponseSchema
>;
export type PostMachaWorkstreamMessageRequest = z.infer<
  typeof postMachaWorkstreamMessageRequestSchema
>;
export type PostMachaWorkstreamMessageResponse = z.infer<
  typeof postMachaWorkstreamMessageResponseSchema
>;
export type MachaMemberResource = z.infer<typeof machaMemberSchema>;
export type MachaOwnerResource = z.infer<typeof machaOwnerSchema>;
export type MachaDecisionCounts = z.infer<typeof machaDecisionCountsSchema>;
export type MachaListObject = z.infer<typeof machaListObjectSchema>;
export type MachaListResponse = z.infer<typeof machaListResponseSchema>;
export type MachaDetailResponse = z.infer<typeof machaDetailResponseSchema>;
export type CreateMachaRequest = z.infer<typeof createMachaRequestSchema>;
export type AddMachaMemberRequest = z.infer<typeof addMachaMemberRequestSchema>;
export type AddMachaMemberResponse = z.infer<typeof addMachaMemberResponseSchema>;
export type MachaArtifactResource = z.infer<typeof machaArtifactSchema>;
export type MachaArtifactListResponse = z.infer<typeof machaArtifactListResponseSchema>;
export type CreateMachaArtifactRequest = z.infer<typeof createMachaArtifactRequestSchema>;
export type UpdateMachaArtifactRequest = z.infer<typeof updateMachaArtifactRequestSchema>;
export type MachaArtifactTurnRequest = z.infer<typeof machaArtifactTurnRequestSchema>;
export type MachaArtifactTurnResponse = z.infer<typeof machaArtifactTurnResponseSchema>;
export type DeleteMachaArtifactResponse = z.infer<typeof deleteMachaArtifactResponseSchema>;
export type MachaAppFileResponse = z.infer<typeof machaAppFileResponseSchema>;
export type MachaSessionResource = z.infer<typeof machaSessionSchema>;
export type MachaSessionListResponse = z.infer<typeof machaSessionListResponseSchema>;
export type CreateMachaSessionRequest = z.infer<typeof createMachaSessionRequestSchema>;
export type MachaMessageResource = z.infer<typeof machaMessageSchema>;
export type MachaMessageListResponse = z.infer<typeof machaMessageListResponseSchema>;
export type PostMachaMessageRequest = z.infer<typeof postMachaMessageRequestSchema>;
export type PostMachaMessageResponse = z.infer<typeof postMachaMessageResponseSchema>;
export type MachaBootstrapResponse = z.infer<typeof machaBootstrapResponseSchema>;
export type MachaPresenceObject = z.infer<typeof machaPresenceObjectSchema>;
export type MachaPresenceListResponse = z.infer<typeof machaPresenceListResponseSchema>;
export type UpdateMachaPresenceRequest = z.infer<typeof updateMachaPresenceRequestSchema>;
export type UpdateMachaPresenceResponse = z.infer<typeof updateMachaPresenceResponseSchema>;
export type MachaEventKind = z.infer<typeof machaEventKindSchema>;
export type MachaEventResource = z.infer<typeof machaEventSchema>;
export type MachaEventListResponse = z.infer<typeof machaEventListResponseSchema>;
export type MachaWatchResource = z.infer<typeof machaWatchSchema>;
export type MachaWatchListResponse = z.infer<typeof machaWatchListResponseSchema>;
export type CreateMachaWatchRequest = z.infer<typeof createMachaWatchRequestSchema>;
export type DeleteMachaWatchResponse = z.infer<typeof deleteMachaWatchResponseSchema>;
export type MachaNotificationResource = z.infer<typeof machaNotificationSchema>;
export type MachaNotificationListResponse = z.infer<
  typeof machaNotificationListResponseSchema
>;
export type MarkMachaNotificationsSeenResponse = z.infer<
  typeof markMachaNotificationsSeenResponseSchema
>;
export type MachaHealthReportBoard = z.infer<typeof machaHealthReportBoardSchema>;
export type MachaHealthReportTotals = z.infer<typeof machaHealthReportTotalsSchema>;
export type MachaHealthReportResponse = z.infer<typeof machaHealthReportResponseSchema>;
export type MachaCommentResource = z.infer<typeof machaCommentSchema>;
export type MachaCommentListResponse = z.infer<typeof machaCommentListResponseSchema>;
export type CreateMachaCommentRequest = z.infer<typeof createMachaCommentRequestSchema>;
export type DeleteMachaCommentResponse = z.infer<typeof deleteMachaCommentResponseSchema>;
